/**
 * Общие canvas-хелперы для лейнового таймлайна (`TrackLanes`) и пианоролла
 * (`PianoRoll`). Раньше `resizeCanvas`, `lowerBound`, `signatureAt`, rAF-цикл
 * и циклы рисования сетки дублировались в двух компонентах — теперь единая
 * точка правды.
 *
 * Оптимизации canvas-рендеринга (карта #8):
 * - Батчинг: линии сетки группируются по стилю в один `Path2D`-путь и один
 *   `stroke()` на стиль (вместо ~435 вызовов `stroke()` на кадр — 4-5).
 * - Старт циклов по `boundaries` через бинарный поиск `lowerBound` вместо
 *   перебора от нуля.
 * - OffscreenCanvas-слой вертикальной сетки (Фаза 2): сетка рисуется в
 *   offscreen-канвасе при изменении зума/контента/вьюпорта, при скролле —
 *   `drawImage` со сдвигом. Feature-detect `OffscreenCanvas` обязателен
 *   (в jsdom/старых браузерах его нет) — фоллбэк на прямое рисование.
 */

import { useEffect, useRef } from "react";
import type { MidiTimeSignature } from "@midi-fantom-utils/midi-core";

declare const process: {
  env: {
    IS_DEV?: string;
  };
};

/** Признак dev-сборки (webpack.DefinePlugin). В jsdom/тестах — `false`. */
export const IS_DEV = process.env.IS_DEV === "true";

export const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

/** Первый индекс массива, где `key(arr[i]) >= target` (массив отсортирован по key). */
export const lowerBound = <T,>(
  arr: ReadonlyArray<T>,
  target: number,
  key: (v: T) => number
): number => {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (key(arr[mid]!) < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/**
 * Time-signature на момент тика (последняя сигнатура с `ticks <= tick`,
 * дефолт 4/4).
 */
export const signatureAt = (
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  tick: number
): [number, number] => {
  let sig: [number, number] = [4, 4];
  for (let i = 0; i < timeSigs.length; i += 1) {
    if (timeSigs[i]!.ticks <= tick) sig = timeSigs[i]!.timeSignature;
    else break;
  }
  return sig;
};

export type CanvasBacking = { w: number; h: number; dpr: number };

/**
 * Подгоняет размер canvas-холста под CSS-размеры с учётом devicePixelRatio
 * (бэк-буфер в пикселях устройства, стиль — в CSS px) и возвращает 2d-контекст
 * в масштабе `dpr`. Возвращает `null`, если контекст недоступен (jsdom,
 * исчерпанные ресурсы) — рисование в таком случае no-op.
 */
export const resizeCanvas = (
  canvas: HTMLCanvasElement,
  w: number,
  h: number,
  cache: { current: CanvasBacking | null }
): CanvasRenderingContext2D | null => {
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext("2d");
  } catch {
    return null;
  }
  if (!ctx) return null;
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const targetW = Math.round(w * dpr);
  const targetH = Math.round(h * dpr);
  const last = cache.current;
  if (!last || last.w !== targetW || last.h !== targetH || last.dpr !== dpr) {
    canvas.width = targetW;
    canvas.height = targetH;
    cache.current = { w: targetW, h: targetH, dpr };
  }
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, w, h);
  return ctx;
};

/**
 * rAF-цикл: перерисовка каждый раз при смене `draw` (после commit), с отменой
 * предыдущего запланированного кадра. Без rAF (jsdom) — синхронный вызов.
 */
export const useRafFrame = (draw: () => void): void => {
  const drawRef = useRef(draw);
  drawRef.current = draw;
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (rafRef.current !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : null;
    if (raf) {
      rafRef.current = raf(() => drawRef.current());
    } else {
      drawRef.current();
    }
    return () => {
      if (rafRef.current !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [draw]);
};

/**
 * Dev-only замер кадра: накапливает время `drawFrame` и логирует среднее раз в
 * `SAMPLES` кадров. В prod/тестах (`IS_DEV = false`) — мгновенный no-op.
 */
const FRAME_SAMPLES = 120;
const frameSamples: Array<{ name: string; sum: number; count: number }> = [];

export const profileDrawFrame = (name: string, startMs: number): void => {
  if (!IS_DEV) return;
  if (typeof performance === "undefined") return;
  let s = frameSamples.find((x) => x.name === name);
  if (!s) {
    s = { name, sum: 0, count: 0 };
    frameSamples.push(s);
  }
  s.sum += performance.now() - startMs;
  s.count += 1;
  if (s.count >= FRAME_SAMPLES) {
    // eslint-disable-next-line no-console
    console.info(`[canvas-perf] ${name}: avg ${(s.sum / s.count).toFixed(2)} ms / frame`);
    s.sum = 0;
    s.count = 0;
  }
};

// --- Батчинг сетки ----------------------------------------------------------

/** Какие подуровни вертикальной сетки рисовать. */
export type TimeGridLevels = {
  /** Долевые линии (gridMed). Всегда есть у обоих компонентов. */
  med?: boolean;
  /** Восьмые (gridFaint, между долями). Рисуются при `beatPx/2 >= ...`. */
  eighths?: boolean;
  /** Минимальный px на долю для показа восьмых (по умолчанию 8 → полу-доля ≥ 4). */
  eighthsMinBeatPx?: number;
  /** Шестнадцатые (gridSub). Рисуются при `beatPx/4 >= ...`. */
  sixteenths?: boolean;
  /** Минимальный px на долю для показа шестнадцатых (по умолчанию 20 → четверть ≥ 5). */
  sixteenthsMinBeatPx?: number;
};

export type TimeGridColors = {
  strong: string;
  med: string;
  faint: string;
  sub: string;
};

export type TimeGridX = {
  strong: number[];
  med: number[];
  faint: number[];
  sub: number[];
};

const collectBeats = (
  mStart: number,
  mEnd: number,
  startOffset: number,
  step: number,
  pxPerTick: number,
  xMin: number,
  xMax: number,
  out: number[]
): void => {
  for (let t = mStart + startOffset; t < mEnd; t += step) {
    const bx = t * pxPerTick;
    if (bx < xMin) continue;
    if (bx > xMax) break;
    out.push(bx);
  }
};

/**
 * Собирает x-координаты вертикальной сетки (world px) внутри [xMin, xMax],
 * сгруппированные по стилю. Учитывает time-signature на каждый такт.
 * `boundaries` — отсортированные тики начала тактов (measureBoundaries).
 */
export const collectTimeGridX = (
  boundaries: ReadonlyArray<number>,
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  ppq: number,
  pxPerTick: number,
  xMin: number,
  xMax: number,
  levels: TimeGridLevels = {}
): TimeGridX => {
  const strong: number[] = [];
  const med: number[] = [];
  const faint: number[] = [];
  const sub: number[] = [];
  if (xMax <= xMin || boundaries.length === 0) {
    return { strong, med, faint, sub };
  }
  let i = lowerBound(boundaries, xMin / pxPerTick, (v) => v);
  for (; i < boundaries.length; i += 1) {
    const mStart = boundaries[i]!;
    const mx = mStart * pxPerTick;
    if (mx > xMax) break;
    strong.push(mx);
    if (i + 1 >= boundaries.length) continue;
    const mEnd = boundaries[i + 1]!;
    const sig = signatureAt(timeSigs, mStart);
    const tpb = (4 / sig[1]) * ppq;
    const beatPx = tpb * pxPerTick;
    if (levels.med !== false) {
      collectBeats(mStart, mEnd, tpb, tpb, pxPerTick, xMin, xMax, med);
    }
    if (levels.eighths && beatPx >= (levels.eighthsMinBeatPx ?? 8)) {
      // Восьмые на «офф-битах»: доля уже отмечена как med, поэтому шаг = tpb,
      // старт со сдвигом на пол-доли.
      collectBeats(mStart, mEnd, tpb / 2, tpb, pxPerTick, xMin, xMax, faint);
    }
    if (levels.sixteenths && beatPx >= (levels.sixteenthsMinBeatPx ?? 20)) {
      // Шестнадцатые: шаг = tpb/2 со сдвигом на четверть доли.
      collectBeats(mStart, mEnd, tpb / 4, tpb / 2, pxPerTick, xMin, xMax, sub);
    }
  }
  return { strong, med, faint, sub };
};

/** Один stroke на группу вертикальных линий (линии от y0 до y1). */
export const strokeVGroup = (
  ctx: CanvasRenderingContext2D,
  xs: ReadonlyArray<number>,
  y0: number,
  y1: number,
  style: string,
  lineWidth = 1
): void => {
  if (xs.length === 0) return;
  ctx.strokeStyle = style;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  for (const x of xs) {
    ctx.moveTo(x, y0);
    ctx.lineTo(x, y1);
  }
  ctx.stroke();
};

/** Один stroke на группу горизонтальных линий (линии от x0 до x1). */
export const strokeHGroup = (
  ctx: CanvasRenderingContext2D,
  ys: ReadonlyArray<number>,
  x0: number,
  x1: number,
  style: string,
  lineWidth = 1
): void => {
  if (ys.length === 0) return;
  ctx.strokeStyle = style;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  for (const y of ys) {
    ctx.moveTo(x0, y);
    ctx.lineTo(x1, y);
  }
  ctx.stroke();
};

/**
 * Рисует вертикальную сетку тактов/долей/субдивизий в мировых координатах
 * (контекст должен быть уже переведён на скролл) внутри [xMin, xMax].
 */
export const renderTimeGrid = (
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  boundaries: ReadonlyArray<number>,
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  ppq: number,
  pxPerTick: number,
  xMin: number,
  xMax: number,
  y0: number,
  y1: number,
  levels: TimeGridLevels,
  colors: TimeGridColors
): void => {
  const g = collectTimeGridX(boundaries, timeSigs, ppq, pxPerTick, xMin, xMax, levels);
  strokeVGroup(ctx as CanvasRenderingContext2D, g.strong, y0, y1, colors.strong);
  strokeVGroup(ctx as CanvasRenderingContext2D, g.med, y0, y1, colors.med);
  strokeVGroup(ctx as CanvasRenderingContext2D, g.faint, y0, y1, colors.faint);
  strokeVGroup(ctx as CanvasRenderingContext2D, g.sub, y0, y1, colors.sub);
};

// --- OffscreenCanvas-слой вертикальной сетки (Фаза 2) ----------------------

/**
 * Кэш вертикальной сетки в offscreen-канвасе. Холст покрывает видимое окно
 * плюс горизонтальный запас `margin` по краям; пере-рендер происходит только
 * при изменении зума/контента/вьюпорта либо когда скролл ушёл за край кэша.
 * При скролле в пределах кэша — `drawImage` со сдвигом.
 */
export type VGridCache = {
  oc: OffscreenCanvas | null;
  octx: OffscreenCanvasRenderingContext2D | null;
  cssW: number;
  cssH: number;
  dpr: number;
  margin: number;
  refX: number;
  key: string;
};

export const createVGridCache = (): VGridCache => ({
  oc: null,
  octx: null,
  cssW: 0,
  cssH: 0,
  dpr: 1,
  margin: 0,
  refX: 0,
  key: "",
});

export const isVGridCacheSupported = (): boolean =>
  typeof OffscreenCanvas !== "undefined";

const getOffscreen2d = (oc: OffscreenCanvas): OffscreenCanvasRenderingContext2D | null => {
  try {
    return oc.getContext("2d");
  } catch {
    return null;
  }
};

/**
 * Гарантирует наличие offscreen-холста нужного размера. Возвращает контекст
 * (или `null`, если OffscreenCanvas недоступен). При пересоздании холста
 * сбрасывает `key`, чтобы следующая отрисовка пере-рендерила сетку.
 */
export const ensureVGridCache = (
  cache: VGridCache,
  cssW: number,
  cssH: number,
  dpr: number,
  margin: number
): OffscreenCanvasRenderingContext2D | null => {
  if (cssW <= 0 || cssH <= 0) return null;
  if (typeof OffscreenCanvas === "undefined") return null;
  const devW = Math.round((cssW + margin * 2) * dpr);
  const devH = Math.max(1, Math.round(cssH * dpr));
  if (
    cache.oc !== null &&
    cache.octx !== null &&
    cache.oc.width === devW &&
    cache.oc.height === devH &&
    cache.dpr === dpr
  ) {
    return cache.octx;
  }
  try {
    const oc = new OffscreenCanvas(devW, devH);
    const octx = getOffscreen2d(oc);
    if (!octx) return null;
    cache.oc = oc;
    cache.octx = octx;
    cache.cssW = cssW;
    cache.cssH = cssH;
    cache.dpr = dpr;
    cache.margin = margin;
    cache.refX = 0;
    cache.key = "";
    return octx;
  } catch {
    return null;
  }
};

/**
 * Пере-рендер сетки в offscreen-холст для скролла `refX`. Контекст холста
 * переводится так, что рисование происходит в мировых координатах, а локальная
 * координата мира `refX - margin` равна 0.
 */
export const renderVGridCache = (
  cache: VGridCache,
  cssW: number,
  cssH: number,
  dpr: number,
  margin: number,
  refX: number,
  boundaries: ReadonlyArray<number>,
  timeSigs: ReadonlyArray<MidiTimeSignature>,
  ppq: number,
  pxPerTick: number,
  levels: TimeGridLevels,
  colors: TimeGridColors
): void => {
  const ctx = cache.octx;
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, cssW + margin * 2, cssH);
  ctx.translate(margin - refX, 0);
  renderTimeGrid(
    ctx,
    boundaries,
    timeSigs,
    ppq,
    pxPerTick,
    refX - margin,
    refX + cssW + margin,
    0,
    cssH,
    levels,
    colors
  );
  cache.cssW = cssW;
  cache.cssH = cssH;
  cache.margin = margin;
  cache.refX = refX;
};

/**
 * Нужен ли пере-рендер кэша: сменился контент/зум (`key`) или скролл ушёл за
 * край закэшированного окна.
 */
export const vGridCacheNeedsRender = (
  cache: VGridCache,
  key: string,
  scrollX: number
): boolean =>
  cache.key !== key || Math.abs(scrollX - cache.refX) > cache.margin;

/**
 * Копирует закэшированную сетку в основной контекст (уже переведённый в
 * мировые координаты translate(-scrollX, -scrollY) и отмасштабированный на
 * dpr). Вертикальная сетка всегда покрывает весь видимый вьюпорт по высоте,
 * поэтому вертикальный скролл не требует пере-рендера.
 */
export const blitVGridCache = (
  ctx: CanvasRenderingContext2D,
  cache: VGridCache,
  scrollY: number
): void => {
  const oc = cache.oc;
  if (!oc) return;
  ctx.drawImage(
    oc as unknown as CanvasImageSource,
    cache.refX - cache.margin,
    scrollY,
    cache.cssW + cache.margin * 2,
    cache.cssH
  );
};
